import React, { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { ArrowLeft, Edit, Truck, FileText, Container, Info, Building2 } from 'lucide-react';
import axios from 'axios';
import { toast } from '../../hooks/use-toast';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const Field = ({ label, value, mono }) => (
  <div className="space-y-0.5">
    <p className="text-[10px] text-gray-400 uppercase tracking-wide">{label}</p>
    <p className={`text-sm font-medium text-gray-800 ${mono ? 'font-mono' : ''}`}>{value || <span className="text-gray-300">-</span>}</p>
  </div>
);

const CarteGriseCard = ({ title, icon: Icon, data, color }) => {
  if (!data || !data.registration) return null;
  const borderColor = color === 'blue' ? 'border-blue-200' : 'border-amber-200';
  const bgColor = color === 'blue' ? 'bg-blue-50/50' : 'bg-amber-50/50';
  const iconColor = color === 'blue' ? 'text-blue-600' : 'text-amber-600';
  const titleColor = color === 'blue' ? 'text-blue-800' : 'text-amber-800';

  return (
    <Card className={`border ${borderColor}`}>
      <CardHeader className={`pb-3 ${bgColor}`}>
        <CardTitle className={`flex items-center gap-2 text-sm ${titleColor}`}>
          <Icon className={`w-4 h-4 ${iconColor}`} />
          {title}
          {data.carteGriseNumber && (
            <Badge variant="secondary" className={`text-[10px] ml-auto ${color === 'blue' ? 'bg-blue-100 text-blue-700' : 'bg-amber-100 text-amber-700'}`}>
              CG {data.carteGriseNumber}
            </Badge>
          )}
        </CardTitle>
      </CardHeader>
      <CardContent className="pt-4 space-y-4">
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="Immatriculation" value={data.registration} mono />
          <Field label="N Carte Grise" value={data.carteGriseNumber} mono />
          <Field label="Proprietaire" value={data.ownerIdentity} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Marque" value={data.brand} />
          <Field label="Type commercial" value={data.commercialType} />
          <Field label="Genre" value={data.genre} />
          <Field label="Carrosserie" value={data.bodyType} />
        </div>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <Field label="Couleur" value={data.color} />
          <Field label="Energie" value={data.energy} />
          <Field label="Usage" value={data.usage} />
          <Field label="Places" value={data.seats} />
        </div>

        <div className={`rounded-lg border p-3 ${color === 'blue' ? 'border-blue-100 bg-blue-50/30' : 'border-amber-100 bg-amber-50/30'}`}>
          <p className={`text-[10px] font-semibold mb-2 flex items-center gap-1 ${color === 'blue' ? 'text-blue-700' : 'text-amber-700'}`}>
            <Info className="w-3 h-3" /> Poids
          </p>
          <div className="grid grid-cols-3 gap-3">
            <Field label="PTAC (Kg)" value={data.ptacKg ? `${data.ptacKg.toLocaleString('fr')} Kg` : null} />
            <Field label="PV (Kg)" value={data.pvKg ? `${data.pvKg.toLocaleString('fr')} Kg` : null} />
            <Field label="CU (Kg)" value={data.cuKg ? `${data.cuKg.toLocaleString('fr')} Kg` : null} />
          </div>
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="Puissance fiscale" value={data.fiscalPower ? `${data.fiscalPower} CV` : null} />
          <Field label="Cylindree" value={data.engineCapacity ? `${data.engineCapacity} CC` : null} />
          <Field label="Essieux" value={data.axles} />
        </div>

        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Field label="Date edition CG" value={data.carteGriseDate} />
          <Field label="1ere mise en circulation" value={data.firstRegistrationDate} />
        </div>

        {(data.vinChassis || data.engineNumber || data.technicalType) && (
          <div className="rounded-lg border border-zinc-100 bg-zinc-50/50 p-3">
            <p className="text-[10px] font-semibold text-zinc-500 mb-2">Verso</p>
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              <Field label="VIN / Chassis" value={data.vinChassis} mono />
              <Field label="Type technique" value={data.technicalType} />
              <Field label="N Moteur" value={data.engineNumber} />
            </div>
            {(data.previousRegistration || data.creditCompany) && (
              <div className="grid grid-cols-2 gap-3 mt-2">
                <Field label="Immat. precedente" value={data.previousRegistration} />
                <Field label="Societe de credit" value={data.creditCompany} />
              </div>
            )}
          </div>
        )}
      </CardContent>
    </Card>
  );
};

const VehicleDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [vehicle, setVehicle] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchVehicle = async () => {
      try {
        const res = await axios.get(`${API}/vehicles/${id}`, {});
        setVehicle(res.data);
      } catch {
        toast({ title: "Erreur", description: "Vehicule introuvable", variant: "destructive" });
        navigate('/admin/vehicles');
      } finally {
        setLoading(false);
      }
    };
    fetchVehicle();
  }, [id, navigate]);

  if (loading) {
    return <div className="flex items-center justify-center h-64"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#1db584]" /></div>;
  }

  if (!vehicle) return null;

  const statusConfig = {
    disponible: { color: 'bg-green-100 text-green-800', label: 'Disponible' },
    occupe: { color: 'bg-amber-100 text-amber-800', label: 'Occupe' },
    en_maintenance: { color: 'bg-gray-100 text-gray-800', label: 'Maintenance' },
    inactif: { color: 'bg-red-100 text-red-800', label: 'Inactif' },
  };
  const st = statusConfig[vehicle.status] || statusConfig.disponible;

  const typeLabels = {
    plateau: 'Plateau', fourgon: 'Fourgon', fourgon_frigo: 'Fourgon Frigo',
    citerne: 'Citerne', benne: 'Benne', porte_conteneur: 'Porte-conteneur', autre: 'Autre'
  };

  // Build tracteur data from vehicle top-level fields
  const tracteurData = {
    registration: vehicle.registration,
    carteGriseNumber: vehicle.carteGriseNumber,
    ownerIdentity: vehicle.ownerIdentity,
    brand: vehicle.brand,
    commercialType: vehicle.commercialType,
    genre: vehicle.genre,
    bodyType: vehicle.bodyType,
    color: vehicle.color,
    energy: vehicle.energy,
    usage: vehicle.usage,
    seats: vehicle.seats,
    ptacKg: vehicle.ptacKg,
    pvKg: vehicle.pvKg,
    cuKg: vehicle.cuKg,
    fiscalPower: vehicle.fiscalPower,
    engineCapacity: vehicle.engineCapacity,
    axles: vehicle.axles,
    carteGriseDate: vehicle.carteGriseDate,
    firstRegistrationDate: vehicle.firstRegistrationDate,
    vinChassis: vehicle.vinChassis,
    engineNumber: vehicle.engineNumber,
    technicalType: vehicle.technicalType,
    previousRegistration: vehicle.previousRegistration,
    creditCompany: vehicle.creditCompany,
  };

  const hasTracteurCG = !!vehicle.carteGriseNumber;
  const hasSemiCG = !!vehicle.semiTrailer?.registration;

  return (
    <div className="space-y-6" data-testid="vehicle-detail">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" size="icon" onClick={() => navigate('/admin/vehicles')} data-testid="back-btn">
            <ArrowLeft className="w-5 h-5" />
          </Button>
          <div className="flex items-center gap-3">
            <div className="w-12 h-12 rounded-xl bg-[#1db584] flex items-center justify-center text-white">
              <Truck className="w-6 h-6" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h1 className="text-xl font-bold text-gray-900 font-mono" data-testid="vehicle-reg">{vehicle.registration}</h1>
                <Badge className={st.color}>{st.label}</Badge>
              </div>
              <p className="text-sm text-gray-500">
                {vehicle.brand && `${vehicle.brand} `}{vehicle.model && `${vehicle.model} `}
                {vehicle.year && `(${vehicle.year})`} - {typeLabels[vehicle.vehicleType] || vehicle.vehicleType}
                {vehicle.capacity && ` - ${vehicle.capacity}T`}
              </p>
            </div>
          </div>
        </div>
        <Button onClick={() => navigate(`/admin/vehicles/${id}/edit`)} className="bg-[#1db584] hover:bg-[#16a075]" data-testid="edit-btn">
          <Edit className="w-4 h-4 mr-2" /> Modifier
        </Button>
      </div>

      {/* Transporteur */}
      {vehicle.partnerName && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-lg border border-emerald-200 bg-emerald-50">
          <Building2 className="w-4 h-4 text-emerald-600" />
          <span className="text-sm font-medium text-emerald-800">Transporteur : {vehicle.partnerName}</span>
        </div>
      )}

      {/* Cartes Grises */}
      {(hasTracteurCG || hasSemiCG) ? (
        <div className={`grid gap-6 ${hasSemiCG ? 'grid-cols-1 lg:grid-cols-2' : 'grid-cols-1'}`}>
          <CarteGriseCard
            title="Carte Grise - Tracteur"
            icon={Truck}
            data={tracteurData}
            color="amber"
          />
          {hasSemiCG && (
            <CarteGriseCard
              title="Carte Grise - Semi-Remorque"
              icon={Container}
              data={vehicle.semiTrailer}
              color="blue"
            />
          )}
        </div>
      ) : (
        <Card className="border-dashed">
          <CardContent className="py-8 text-center">
            <FileText className="w-8 h-8 text-gray-300 mx-auto mb-2" />
            <p className="text-sm text-gray-400">Aucune carte grise renseignee</p>
            <Button variant="outline" size="sm" className="mt-3" onClick={() => navigate(`/admin/vehicles/${id}/edit`)}>
              Ajouter les informations CG
            </Button>
          </CardContent>
        </Card>
      )}

      {/* Commentaire */}
      {vehicle.internalComment && (
        <Card>
          <CardContent className="pt-4">
            <p className="text-[10px] text-gray-400 uppercase tracking-wide mb-1">Commentaire interne</p>
            <p className="text-sm text-gray-700">{vehicle.internalComment}</p>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default VehicleDetail;
